// AddContactLinkModal.js - Complete Fixed Version
import React, { useState, useContext, useEffect } from 'react';
import { Modal, Box, Typography, IconButton, TextField, InputAdornment, Divider, Button, Backdrop } from '@mui/material';
import { CloseIcon, SearchIcon } from 'icons/materialIcons';
import ContactLinksCategorySection from './ContactLinkCategorySection';
import ContactLinksDetailModal from './ContactLinkDetailModal';
import { ContactLinkBaseUrlContext } from 'contexts/ContactLinkBaseUrlContext';
import { useUserContext } from 'contexts/UserContext';
import { useCardContext } from 'contexts/CardContext';
import { useContactLinkContext } from 'contexts/ContactLinkContext';
import { useParams } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import * as Sentry from '@sentry/react';

const AddContactLinksModal = ({
  open,
  handleClose,
  isTemporaryCard = false
}) => {
  const [contactLinksDetailModalOpen, setContactLinksDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { cardId: urlCardId } = useParams();
  const { authState } = useAuth();
  const { categories, types } = useContext(ContactLinkBaseUrlContext);
  const [contactLinkType, setContactLinkType] = useState('');
  const { userState } = useUserContext();
  const { cardId: tempCardId } = useCardContext();
  const { isInSignupFlow, forceRefetchCard, currentCardId, contactLinks } = useContactLinkContext(); // ✅ ADD FORCE REFETCH

  // FIXED: Use the correct cardId based on the flow
  // During signup (isTemporaryCard), use tempCardId from context
  // During normal operations, use URL cardId
  const cardId = isTemporaryCard ? tempCardId : urlCardId;

  // FIXED: Get userId consistently for both flows
  const userId = authState?.user?.userId || null;

  useEffect(() => {
    const debugInfo = {
      isTemporaryCard,
      urlCardId,
      tempCardId,
      finalCardId: cardId,
      userId: userId ? userId.substring(0, 10) + '...' : 'null',
      authState_exists: !!authState,
      authState_user_exists: !!authState?.user
    };

    // Console log for development
    console.log('🔍 AddContactLinkModal: Debug info:', debugInfo);

    // Send to Sentry only in production
    if (process.env.NODE_ENV === 'production') {
      Sentry.addBreadcrumb({
        category: 'contact-link-modal',
        message: 'AddContactLinkModal Debug Info',
        data: debugInfo,
        level: 'info'
      });
    }
  }, [isTemporaryCard, urlCardId, tempCardId, cardId, userId, authState]);

  const handleContactLinkButtonClick = (item) => {
    const debugInfo = {
      item,
      isTemporaryCard,
      userId: userId ? userId.substring(0, 10) + '...' : 'null',
      cardId,
      urlCardId,
      tempCardId,
      contactLinksCount: contactLinks?.length || 0
    };

    // Console log for development
    console.log('🔍 AddContactLinkModal: handleContactLinkButtonClick called with:', item);
    console.log('🔍 AddContactLinkModal: Current state:', debugInfo);
    console.log('🔍 AddContactLinkModal: Debug info:', {
      isTemporaryCard,
      urlCardId,
      tempCardId,
      userId: userId?.substring(0, 10) + '...',
      contactLinksCount: contactLinks?.length || 0
    });

    // Send to Sentry only in production
    if (process.env.NODE_ENV === 'production') {
      Sentry.addBreadcrumb({
        category: 'contact-link-modal',
        message: 'Contact Link Button Click',
        data: debugInfo,
        level: 'info'
      });
    }

    setContactLinkType(item);
    setSelectedItem(item);
    setContactLinksDetailModalOpen(true);
  };

  useEffect(() => {
    console.log('userState for testing', userState?.userName);
  }, [userState]);

  const scrollToCategory = (category) => {
    const categoryElement = document.getElementById(category);
    if (categoryElement) {
      categoryElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  // Filter categories and items based on the search term
  const filteredCategories = categories.filter(category =>
    category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTypes = Object.keys(types).reduce((acc, category) => {
    acc[category] = types[category].filter(item =>
      item.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return acc;
  }, {});

  // ✅ NEW: Handle modal close with force refetch for database sync cards
  const handleContactLinkDetailModalClose = () => {
    console.log('🔍 AddContactLinkModal: Closing ContactLinksDetailModal');

    // Send to Sentry only in production
    if (process.env.NODE_ENV === 'production') {
      Sentry.addBreadcrumb({
        category: 'contact-link-modal',
        message: 'Closing ContactLinksDetailModal',
        data: {
          contactLinkType,
          cardId,
          userId: userId ? userId.substring(0, 10) + '...' : 'null'
        },
        level: 'info'
      });
    }

    setContactLinksDetailModalOpen(false);

    // ✅ CRITICAL FIX: Force refetch card data after potential contact link creation
    // Only for non-temporary cards and when we have a valid cardId
    const targetCardId = currentCardId || cardId;
    const isSignupFlowActive = typeof isInSignupFlow === 'function' ? isInSignupFlow() : false;

    if (targetCardId && !isTemporaryCard && !isSignupFlowActive) {
      console.log('🔍 AddContactLinkModal: Force refetching card after modal close:', targetCardId);

      // ✅ SIMPLIFIED: Reduced delay since rate limiting is not needed
      setTimeout(() => {
        try {
          forceRefetchCard(targetCardId);
        } catch (refetchError) {
          console.error('🔍 AddContactLinkModal: Force refetch failed:', refetchError);
          // Don't throw - this is not critical for the user experience
        }
      }, 500); // Reduced to 500ms since rate limiting is not needed
    } else {
      console.log('🔍 AddContactLinkModal: Skipping force refetch - temporary card or signup flow');
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="contact-links-modal"
        disableScrollLock
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(8px)',
            bgcolor: 'rgba(245, 245, 245, 0.7)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '55%',
            maxWidth: 1000,
            height: '85%',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            overflowY: 'auto',
            p: 3,
            outline: 'none',
          }}
        >
          {/* Header with Close Icon */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              Add content
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Input */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="body2" sx={{ flex: 1, color: '#666' }}>
              Select from our wide variety of links and contact info below.
            </Typography>

            <TextField
              variant="outlined"
              size="small"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: '100%', maxWidth: 250, ml: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Divider to separate sections */}
          <Divider sx={{ mb: 2 }} />

          {/* Button Section (Filtered Categories) */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {filteredCategories.map((category, index) => (
              <Button
                key={category}
                variant="outlined"
                sx={{
                  backgroundColor: '#f5f5f5',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  borderColor: '#ddd',
                  color: '#555',
                  '&:hover': {
                    backgroundColor: '#ececec',
                  },
                }}
                onClick={() => scrollToCategory(category)}
              >
                {category}
              </Button>
            ))}
          </Box>

          {/* Render Filtered ContactLinksCategorySection for each filtered category */}
          {filteredCategories.map((category) => (
            <Box
              key={category}
              id={category}
              sx={{
                mb: 4,
                display: 'flex',
                justifyContent: 'center',
                px: 3,
              }}
            >
              <Box
                sx={{
                  maxWidth: 800,
                  width: '100%',
                }}
              >
                <ContactLinksCategorySection
                  categoryTitle={category}
                  items={filteredTypes[category] || []}
                  onContactLinkButtonClick={handleContactLinkButtonClick}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Modal>

      {/* ✅ FIXED: ContactLinksDetailModal with force refetch on close */}
      <ContactLinksDetailModal
        open={contactLinksDetailModalOpen}
        handleClose={handleContactLinkDetailModalClose} // ✅ Use the enhanced close handler
        userId={userId} // FIXED: Always pass userId (same as RecommendedLinks flow)
        cardId={isTemporaryCard ? urlCardId : cardId} // ✅ FIXED: Use UUID for new card creation
        contactLinkType={contactLinkType}
        existingContactLinks={contactLinks || []} // ✅ ADD: Pass contact links from context
        isTemporaryCard={isTemporaryCard}
        isInSignupFlow={isInSignupFlow}
      />
    </>
  );
};

export default AddContactLinksModal;